/**
 * Every "Cells show" option of the Scorecard's month-by-month grid in one file
 * (review #178, 2026-10-07: Max had to pick each option, export it, and pick the next).
 *
 * Two shapes, the same numbers: an Excel workbook with a sheet per option, each sheet
 * exactly that option's export; and one CSV, a person per row, with a column group per
 * option ("Cost per claim · Jan" … "Cost per claim · Average"). Each option's cells
 * come from `monthGridRows`, the function the grid itself draws, so nothing in the file
 * can differ from what the option shows on screen. Raw values, as every export here.
 */
import type { Person } from '@/data/selectors'
import type { MonthScorer } from '@/measures/monthScore'
import { MONTH_MAX } from '@/measures/monthScore'
import type { ExportColumn, XLSXSheet } from '@/ui/export'
import {
  MONTH_METRICS,
  SCORE_KEY_OF,
  monthGridRows,
  monthGridTotalHeader,
  type GridMode,
  type MonthGridRow,
  type MonthMetric,
} from './EmployeeMonthGrid'

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

export interface GridOption {
  /** As the picker reads, plus "· points" for a measure's points view. */
  name: string
  metric: MonthMetric
  mode: GridMode
}

/**
 * The picker's options, in its order: the month score, each scored measure as the
 * measure and as its points, then the other measures.
 */
export function gridOptions(): GridOption[] {
  const scored = MONTH_METRICS.filter((m) => m.group === 'scorecard')
  const other = MONTH_METRICS.filter((m) => m.group === 'other')
  return [
    { name: `Month score (of ${MONTH_MAX})`, metric: scored[0]!, mode: 'score' },
    ...scored.flatMap((m): GridOption[] =>
      SCORE_KEY_OF[m.key]
        ? [
            { name: m.label, metric: m, mode: 'value' },
            { name: `${m.label} · points`, metric: m, mode: 'points' },
          ]
        : [{ name: m.label, metric: m, mode: 'value' }],
    ),
    ...other.map((m): GridOption => ({ name: m.label, metric: m, mode: 'value' })),
  ]
}

/**
 * A sheet's tab name: Excel allows 31 characters, and "Claims billed vs resolved % ·
 * points" is 36. The long labels get a short form; everything else is the picker's.
 */
const SHEET_SHORT: Record<string, string> = {
  rvb: 'Billed vs resolved %',
  wk: 'Open worked in month %',
  coll: 'Collection rate',
  twoPlus: 'Two+ touches',
  touches: 'Touches per touched claim',
}
const sheetName = (o: GridOption) => {
  if (o.mode === 'score') return o.name
  const base = SHEET_SHORT[o.metric.key] ?? o.metric.label
  return o.mode === 'points' ? `${base} pts` : base
}

const monthHead = (m: string, openMonths: readonly string[]) =>
  MONTH_LABEL(m) + (openMonths.includes(m) ? ' *' : '')

const who: ExportColumn<MonthGridRow>[] = [
  { header: 'Employee', value: (r) => r.person.employee },
  { header: 'Paylocity ID', value: (r) => r.person.paylocityId ?? '' },
  { header: 'Job title', value: (r) => r.person.jobTitle },
  { header: 'Department', value: (r) => r.person.department },
]

/** A sheet per option, each the columns that option's own export carries. */
export function allOptionSheets(
  scorer: MonthScorer,
  people: readonly Person[],
  months: readonly string[],
  openMonths: readonly string[],
): XLSXSheet<MonthGridRow>[] {
  const byName = (a: MonthGridRow, b: MonthGridRow) => a.person.employee.localeCompare(b.person.employee)
  return gridOptions().map((o) => ({
    name: sheetName(o),
    rows: monthGridRows(o.metric, o.mode, scorer, people, months).sort(byName),
    columns: [
      ...who,
      ...months.map((m, i) => ({
        header: monthHead(m, openMonths),
        value: (r: MonthGridRow) => r.cells[i] ?? null,
      })),
      { header: monthGridTotalHeader(o.metric, o.mode), value: (r: MonthGridRow) => r.total },
    ],
  }))
}

/** One row per person, a column group per option: the single-CSV shape. */
export function allOptionsTable(
  scorer: MonthScorer,
  people: readonly Person[],
  months: readonly string[],
  openMonths: readonly string[],
): { rows: Person[]; columns: ExportColumn<Person>[] } {
  const options = gridOptions().map((o) => ({
    o,
    rows: new Map(
      monthGridRows(o.metric, o.mode, scorer, people, months).map((r) => [r.person.employee, r]),
    ),
  }))
  const rows = people
    .filter((p) => p.months.length > 0)
    .slice()
    .sort((a, b) => a.employee.localeCompare(b.employee))
  const columns: ExportColumn<Person>[] = [
    { header: 'Employee', value: (p) => p.employee },
    { header: 'Paylocity ID', value: (p) => p.paylocityId ?? '' },
    { header: 'Job title', value: (p) => p.jobTitle },
    { header: 'Department', value: (p) => p.department },
    ...options.flatMap(({ o, rows: byName }) => [
      ...months.map((m, i) => ({
        header: `${o.name} · ${monthHead(m, openMonths)}`,
        value: (p: Person) => byName.get(p.employee)?.cells[i] ?? null,
      })),
      {
        header: `${o.name} · ${monthGridTotalHeader(o.metric, o.mode)}`,
        value: (p: Person) => byName.get(p.employee)?.total ?? null,
      },
    ]),
  ]
  return { rows, columns }
}
